import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, Calendar, CreditCard, LogOut, Stethoscope, ClipboardList, UserCog, BarChart3, CalendarClock, Building2, DollarSign, Book, ListOrdered, Package } from 'lucide-react';
import GlobalSearch from './GlobalSearch';

const Layout = ({ children }) => {
  const { user, logout, canAccessBilling, canAccessClinical, isOwner, isDoctor, isReception } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Check if user is cashier
  const isCashier = user?.role === 'cashier';
  const isInventoryManager = user?.role === 'inventory_manager';
  const isNurse = user?.role === 'nurse';

  // Build navigation items based on role
  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, show: true },
    // Patient Queue - Reception, Owner, Doctor, Nurse
    { path: '/queue', label: 'Patient Queue', icon: ListOrdered, show: isOwner || isReception || isDoctor || isNurse },
    { path: '/patients', label: 'Patients', icon: Users, show: !isCashier && !isInventoryManager },
    // Appointments - Doctor sees "My Schedule", others see "Appointments"
    { path: '/appointments', label: isDoctor ? 'My Schedule' : 'Appointments', icon: CalendarClock, show: !isCashier && !isInventoryManager },
    // Clinical encounters - only for owner, doctor, and nurse
    { path: '/encounters', label: 'Encounters', icon: Calendar, show: canAccessClinical },
    // Tasks - visible to all staff except cashier and inventory manager
    { path: '/tasks', label: 'Tasks', icon: ClipboardList, show: !isCashier && !isInventoryManager },
    // Billing - owner, reception, and cashier
    { path: '/billing', label: 'Billing', icon: CreditCard, show: canAccessBilling || isCashier },
    // Outstanding Balances - Cashier view (Phase 6.5)
    { path: '/outstanding-balances', label: 'Collections', icon: DollarSign, show: isCashier || isOwner },
    // Inventory Management - Owner only (Packet A)
    { path: '/inventory', label: 'Inventory', icon: Package, show: isOwner },
    // Insurance Providers - only for owner (Phase 6.4)
    { path: '/insurance-providers', label: 'Insurers', icon: Building2, show: isOwner },
    // Productivity - only for owner
    { path: '/productivity', label: 'Productivity', icon: BarChart3, show: isOwner },
    // Staff Management - only for owner
    { path: '/staff', label: 'Staff', icon: UserCog, show: isOwner },
    // Library - All authenticated users (Phase 6.5)
    { path: '/library', label: 'Library', icon: Book, show: true },
  ].filter(item => item.show);

  // Get role display name
  const getRoleDisplay = (role) => {
    switch (role) {
      case 'owner': return 'Owner Admin';
      case 'doctor': return 'Doctor';
      case 'nurse': return 'Nurse';
      case 'reception': return 'Reception';
      case 'cashier': return 'Cashier';
      case 'inventory_manager': return 'Inventory Mgr';
      default: return role || 'User';
    }
  };

  return (
    <div className="flex min-h-screen" data-testid="app-layout">
      {/* Sidebar */}
      <aside className="sidebar" data-testid="sidebar">
        <div className="p-6 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
              <Stethoscope className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white font-heading">My Med Pal</h1>
              <p className="text-xs text-slate-400">Medical Practice</p>
            </div>
          </div>
        </div>

        <nav className="flex-1 py-6 overflow-y-auto scrollbar-thin scrollbar-thumb-slate-700 scrollbar-track-slate-800" data-testid="navigation">
          {navItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={({ isActive }) =>
                `sidebar-link ${isActive ? 'sidebar-link-active' : 'sidebar-link-inactive'}`
              }
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-4 border-t border-slate-800">
          <div className="flex items-center gap-3 mb-4 px-2">
            <div className="w-8 h-8 bg-slate-700 rounded-full flex items-center justify-center text-sm font-medium text-white">
              {user?.full_name?.charAt(0) || 'U'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400">{getRoleDisplay(user?.role)}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="sidebar-link sidebar-link-inactive w-full"
          >
            <LogOut className="w-5 h-5" />
            Logout
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content" data-testid="main-content">
        {/* Global Search Bar */}
        <div className="sticky top-0 z-40 bg-slate-50 border-b border-slate-200 px-6 py-3">
          <GlobalSearch />
        </div>
        <div className="p-0">
          {children}
        </div>
      </main>
    </div>
  );
};

export default Layout;
